import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Building2, MapPin, Sparkles, ArrowRight, CheckCircle2,
  Mail, ShieldCheck, Briefcase, Sun, Moon,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HIGHLIGHTS = [
  { icon: Building2, label: 'Premium Bangalore Address' },
  { icon: Mail, label: 'Mail & Courier Handling' },
  { icon: ShieldCheck, label: 'GST Registration Support' },
  { icon: CheckCircle2, label: 'Compliance-ready Docs' },
];

const VirtualOfficeIndexPage = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/virtual-office/venues`);
        setVenues(res.data || []);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refParam = user?.id ? `?ref=${user.id}` : '';

  return (
    <div className="min-h-screen bg-background" data-testid="vo-index-page">
      {/* Top nav */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}>
            <img src="/logo.webp" alt="Clayworks" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            {!isAuthenticated && <Button variant="outline" size="sm" onClick={() => navigate('/login')}>Sign In</Button>}
            {isAuthenticated && <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>My Dashboard</Button>}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge variant="outline" className="mb-3"><Sparkles className="w-3 h-3 mr-1" /> Virtual Office</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Choose Your <span className="text-primary">Clayworks</span> Address
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
            Premium Bangalore business address, GST registration support, mail handling, compliance documentation — without leasing a physical office.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <h.icon className="w-4 h-4 text-primary" /> {h.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Centre selection */}
      <section className="px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-2xl font-bold">Pick a Centre</h2>
              <p className="text-sm text-muted-foreground mt-1">View plans and pricing for any of our virtual office locations.</p>
            </div>
            <p className="text-xs text-muted-foreground">{venues.length} centre{venues.length !== 1 ? 's' : ''} available</p>
          </div>

          {loading && <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

          {!loading && venues.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <h3 className="font-heading text-lg font-semibold mb-1">No Virtual Office plans available yet</h3>
                <p className="text-sm text-muted-foreground mb-4">We're rolling out across centres soon. Drop us a note to be the first to know.</p>
                <Button onClick={() => navigate('/contact')} variant="outline">Contact Sales</Button>
              </CardContent>
            </Card>
          )}

          {!loading && venues.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {venues.map((v) => (
                <Card key={v.id} className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/preview/virtual-office/${v.id}${refParam}`)} data-testid={`vo-index-venue-${v.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-bold truncate">{v.name}</h3>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 truncate mt-0.5">
                          <MapPin className="w-3 h-3" /> {v.region || v.address || 'Bangalore'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <Badge variant="outline" className="text-xs">{v.active_vo_tiers} plan{v.active_vo_tiers !== 1 ? 's' : ''} available</Badge>
                      <span className="text-sm text-primary font-medium group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                        View Plans <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VirtualOfficeIndexPage;
